import { Cents, Heading, LinkButton, RatingStarIcon, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

const MIN_REVIEWS_FOR_RATING = 5

interface SpotlightPosterProps {
  image: string
  imageCrop?: ImageCrop
  imageAlt?: string
  // The phone crop (345:288). Without it the wide image is centre-cropped down to it.
  imageTall?: string
  eyebrow?: string
  lines?: string[]
  title: string
  packshot?: { image: string; alt: string }
  price?: string
  was?: string
  quincena?: { lead: string; amount: string; tail?: string }
  rating?: number
  reviewCount?: number
  cta: string
  ctaHref?: string
  priority?: boolean
}

export const SpotlightPoster = ({
  image,
  imageCrop,
  imageAlt,
  imageTall,
  eyebrow,
  lines,
  title,
  packshot,
  price,
  was,
  quincena,
  rating,
  reviewCount,
  cta,
  ctaHref,
  priority,
}: SpotlightPosterProps) => {
  const headline = lines && lines.length > 0 ? lines : undefined
  const showRating = rating != null && (reviewCount ?? MIN_REVIEWS_FOR_RATING) >= MIN_REVIEWS_FOR_RATING

  return (
    <div className="px-gut @container">
      <div className="relative @3xl:grid @3xl:grid-cols-1">
        <div className="press-banner rounded-10x bg-surface-newgray aspect-poster @2xl:aspect-poster-wide relative overflow-hidden @3xl:col-start-1 @3xl:row-start-1 @3xl:w-full @3xl:self-stretch">
          {imageTall && <TileImage src={imageTall} alt={imageAlt ?? ""} fill sizes="100vw" priority={priority} className="object-cover @2xl:hidden" />}
          <TileImage
            src={image}
            crop={imageCrop}
            alt={imageAlt ?? ""}
            fill
            sizes="(max-width: 1024px) 800px, 1280px"
            priority={priority}
            className={`object-cover ${imageTall ? "hidden @2xl:block" : ""}`}
          />
          <CoverLink href={cta ? ctaHref : undefined} />
          {(eyebrow || headline) && (
            <div className="text-shadow-poster pointer-events-none absolute top-500 right-500 left-500 flex flex-col gap-250 @3xl:top-1250 @3xl:left-1250 @3xl:max-w-1/2">
              {eyebrow && (
                <Text as="span" variant="body" family="text" tone="inverted" className="@3xl:text-body-lg">
                  {eyebrow}
                </Text>
              )}
              {headline && (
                <Heading as="h2" variant="heroSplit" family="hero" tone="inverted" className="tracking-poster block">
                  {headline.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </Heading>
              )}
            </div>
          )}
        </div>

        <div className="rounded-7-5x bg-surface shadow-tile mt-250 flex flex-col p-500 @3xl:relative @3xl:col-start-1 @3xl:row-start-1 @3xl:m-1250 @3xl:w-69 @3xl:self-end @3xl:justify-self-end">
          <div className="flex gap-500 @3xl:flex-col @3xl:gap-0">
            {packshot && (
              <div className="@3xl:aspect-packshot relative h-24 w-25.5 shrink-0 @3xl:h-auto @3xl:w-full">
                <TileImage src={packshot.image} alt={packshot.alt} fill sizes="(max-width: 1024px) 102px, 235px" className="object-contain" />
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col @3xl:flex-none">
              {(quincena || showRating) && (
                <Text as="p" variant="bodySm" family="text" tone="brand" className="text-little @3xl:text-little-lg order-3 mt-150 tabular-nums @3xl:order-1 @3xl:mt-250">
                  {quincena && (
                    <>
                      {quincena.lead} <Cents>{quincena.amount}</Cents>
                      {quincena.tail ? ` ${quincena.tail}` : null}
                    </>
                  )}
                  {showRating && (
                    <>
                      {quincena ? " · " : null}
                      {rating.toFixed(1)}
                      <RatingStarIcon size={9} className="ml-50 inline align-baseline" aria-hidden />
                    </>
                  )}
                </Text>
              )}
              {price && (
                <p className="font-text order-1 flex flex-wrap items-baseline gap-150 @3xl:order-2 @3xl:mt-50">
                  <span className="text-price-sm @3xl:text-price-md text-fg-strong tabular-nums">
                    <Cents>{price}</Cents>
                  </span>
                  {was && (
                    <span className="text-tab text-fg-strike strike tabular-nums">
                      <Cents>{was}</Cents>
                    </span>
                  )}
                </p>
              )}
              <Text as="p" variant="body" family="text" tone="strong" className="text-copy @3xl:text-copy-lg order-2 line-clamp-2 @3xl:order-3">
                {title}
              </Text>
            </div>
          </div>
          <LinkButton href={ctaHref} fullWidth className="font-text text-copy-lg text-surface-sand mt-500 font-normal @3xl:mt-750">
            {cta}
          </LinkButton>
        </div>
      </div>
    </div>
  )
}
